/** Normalise for fuzzy comparison (same idea as geoLabel). */
export function compactGeoKey(s: string): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Classic Levenshtein distance (small strings only). */
export function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const row = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) row[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = row[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost);
      prev = tmp;
    }
  }
  return row[n];
}

/** Similarity in 0..1 (1 = identical). */
export function stringSimilarity(a: string, b: string): number {
  const x = compactGeoKey(a);
  const y = compactGeoKey(b);
  if (!x.length && !y.length) return 1;
  if (!x.length || !y.length) return 0;
  if (x === y) return 1;
  const d = levenshtein(x, y);
  const maxLen = Math.max(x.length, y.length);
  return 1 - d / maxLen;
}

/**
 * Best string from candidates by similarity to `query`.
 * Returns null if best score is below `minScore`.
 */
export function bestFuzzyMatch<T>(
  query: string,
  candidates: T[],
  getLabel: (t: T) => string,
  minScore: number,
): { item: T; score: number } | null {
  let best: T | null = null;
  let bestScore = 0;
  for (const c of candidates) {
    const s = stringSimilarity(query, getLabel(c));
    if (s > bestScore) {
      bestScore = s;
      best = c;
    }
  }
  if (best && bestScore >= minScore) return { item: best, score: bestScore };
  return null;
}
